import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, FileWarning } from "lucide-react";
import api from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "ora";
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  return `${Math.floor(s / 86400)} gg fa`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const computePos = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const mobile = window.innerWidth < 480;
    setPos({
      top: Math.round(r.bottom + 8),
      right: mobile ? 8 : Math.round(Math.max(8, window.innerWidth - r.right)),
      left: mobile ? 8 : null,
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    computePos();
    const onResize = () => computePos();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, computePos]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
      load();
    }
    if (n.order_code) navigate(`/production/order/${encodeURIComponent(n.order_code)}`);
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* noop */ }
    load();
  };

  return (
    <div className="relative">
      <button data-testid="notif-bell" ref={btnRef} onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative t-head p-2 rounded-[3px] hover:bg-[#eef2fb] transition-colors">
        <Bell size={20} />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#9c1e2b] text-white text-[10px] font-700 flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && pos && createPortal(
        <div data-testid="notif-panel" ref={panelRef}
          style={{ position: "fixed", top: pos.top, right: pos.right, left: pos.left != null ? pos.left : "auto" }}
          className="w-auto sm:w-[340px] panel rounded-[4px] shadow-lg z-[70] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b hairline bg-[#f7f9fc]">
            <span className="font-display font-600 text-sm t-head">Notifiche</span>
            {unread > 0 && (
              <button data-testid="notif-read-all" onClick={markAll} className="text-[11px] t-muted hover:t-head inline-flex items-center gap-1">
                <CheckCheck size={13} /> Segna tutte lette
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y hairline">
            {items.length === 0 && <div className="py-8 text-center t-faint text-sm">Nessuna notifica</div>}
            {items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 hover:bg-[#f7f9fc] transition-colors flex gap-2.5 ${n.read ? "" : "bg-[#eff4ff]"}`}>
                <FileWarning size={16} className="text-[#b45309] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] t-body leading-snug break-words">{n.message}</div>
                  <div className="text-[10px] t-faint mt-1 font-mono">{n.created_by} · {timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#33559d] mt-1 shrink-0" />}
              </button>
            ))}
          </div>
        </div>, document.body)}
    </div>
  );
}
